import { Link, useLocation, useNavigate } from "react-router-dom";
import { useActiveBrand } from "../../hooks/useActiveBrand";
import { buildBrandScopedPath, buildSwitchedBrandPath, isGlobalOperatorPath } from "../../lib/brand-navigation";
import { isV2WorkspaceRoute } from "../../lib/v2-routes";
import { BrandSwitcher } from "./BrandSwitcher";
import type { Brand } from "../../../../shared/types";

/** Sticky strip below header on small screens when user has multiple brands. */
export function MobileBrandBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { brands, activeBrand, activeBrandId, setActiveBrandId, hasMultipleBrands, isLoading, isError } = useActiveBrand();
  const compactChrome = isV2WorkspaceRoute(location.pathname);

  function handleBrandChange(id: string) {
    const switchedPath = buildSwitchedBrandPath(location.pathname, id);
    if (switchedPath) {
      navigate(switchedPath);
      return;
    }
    if (isGlobalOperatorPath(location.pathname)) {
      navigate(buildBrandScopedPath(`${location.pathname}${location.search}`, id));
      return;
    }
    setActiveBrandId(id);
  }

  return (
    <MobileBrandBarView
      brands={brands}
      activeBrand={activeBrand}
      activeBrandId={activeBrandId}
      hasMultipleBrands={hasMultipleBrands}
      isLoading={isLoading}
      isError={isError}
      compactChrome={compactChrome}
      onChange={handleBrandChange}
    />
  );
}

export function MobileBrandBarView({
  brands,
  activeBrand,
  activeBrandId,
  hasMultipleBrands,
  isLoading,
  isError,
  compactChrome,
  onChange,
}: {
  brands: Brand[];
  activeBrand: Brand | undefined;
  activeBrandId: string | undefined;
  hasMultipleBrands: boolean;
  isLoading: boolean;
  isError: boolean;
  compactChrome: boolean;
  onChange: (id: string) => void;
}) {

  // V2 main has no horizontal padding (px-0), so drop the legacy negative margin there.
  const wrapClass = compactChrome
    ? "sticky top-0 z-20 border-b border-slate-200/80 bg-slate-100/95 px-3 py-2 backdrop-blur md:hidden"
    : "sticky top-0 z-20 -mx-4 border-b border-slate-200/80 bg-slate-100/95 px-4 py-3 backdrop-blur md:hidden";

  return (
    <div className={wrapClass}>
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="min-w-0">
          <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
            Active brand
          </p>
          <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900" title={activeBrand?.name}>
            {isLoading ? "Loading brands…" : isError ? "Brands unavailable" : activeBrand?.name ?? "No active brand"}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {hasMultipleBrands ? (
            <BrandSwitcher id="mobile-active-brand-select" brands={brands} value={activeBrandId} onChange={onChange} className="max-w-[13rem]" />
          ) : null}
          <Link className="text-sm font-medium text-brand-700 hover:text-brand-800" to="/brands">
            Manage brands<span aria-hidden="true"> →</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
